var template = document.createElement('template');
template.innerHTML = `
  <style>
    :host {
        box-sizing: border-box;
        --color-dialog-text: #0D232B;
        --color-dialog-backdrop: rgba(0,0,0,0.35);
    }

    :host([data-width="small"]) dialog {
        width: min(500px, 100vw);
    }
        
    :host([data-width="medium"]) dialog {
        width: min(700px, 100vw);
    }
              
    :host([data-width="large"]) dialog {
        width: min(900px, 100vw);
    }
            
    :host([data-width="xlarge"]) dialog {
        width: min(1200px, 100vw);
    }

    :host * {
      box-sizing: inherit;
    }

    dialog {
        position: relative;
        border: 0;
        padding: 0;
        background-color: var(--clr-white);
        border-radius: 0.5rem;
        box-shadow: 0 0 7px 3px rgba(0,0,0,0.25);
        transform: scale(.9);
        transition: opacity 0.2s ease-out, transform 0.2s ease-out;
    }

    dialog .header {
      position: sticky;
      z-index: 1;
      width: calc(100% + 40px);
      background-color: #fff;
      top: 0;
      border-bottom: 1px solid #ccc;
      margin-top: -1.5rem;
      margin-left: -2rem;
      padding: 1.5rem 2rem;
    }

    dialog::backdrop {
        background-color: var(--color-dialog-backdrop);
    }

    dialog,
    dialog[open]::backdrop {
        opacity: 0;
        transition: opacity 0.2s ease-out, transform 0.2s ease-out;
    }

    dialog[open].fade-in {
        transform: scale(1);
    }

    dialog[open].fade-in,
    dialog[open].fade-in::backdrop {
        transition-delay: 0s;
    }

    dialog[open].fade-in,
    dialog[open].fade-in::backdrop {
        opacity: 1;
    }

    dialog .header {
        display: flex;
        justify-content: space-between;
    }

    dialog .title {
        flex: 1;
        margin: 0;
        font-size: 3rem;
        font-weight: 700;
        color: var(--color-dialog-text);
    }

    dialog .close-button {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: 0;
        color: #fff;
        background-color: #0D232B;
        box-shadow: 0 0 5px rgba(0,0,0,0.5);
        flex-shrink: 0;
        cursor: pointer;
        transition: background-color 0.2s ease-out;
    }

    dialog .close-button:hover {
      background-color: rgba(var(--color-button));
    }

    dialog .close-button:active {
        border: 0;
    }

    ::slotted([slot="close-button"]) {
        position: relative;
        left: 0;
        font-size: 2.5rem;
    }

    dialog .content {
        padding: 1.5rem 2rem 2rem;
        background-color: #fff;
    }

    dialog .footer {
        margin-top: 3rem;
    }

    @media screen and (min-width: 1100px) {
        dialog .title {
            font-size: 3.5rem;
        }

        dialog .close-button {
            position: relative;
            top: 5px;
        }

        dialog .header {
            width: calc(100% + 8rem);
            padding: 1.5rem 2rem 1.5rem 4rem;
            margin-left: -4rem;
        }

        dialog .content {
            padding: 1.5rem 4rem 3rem;
        }
    }
  </style>
  <dialog>
    <div class="content">
      <div class="header">
          <h2 class="title"></h2>
          <button class="close-button" data-close-button>
            <slot name="close-button"></slot>
          </button>
      </div>
      <slot></slot>

      <div class="footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </dialog>
`;

var EventModalClosed = 'modal-closed';
var EventToggleOpen = 'toggle-open';
var EventToggleOpenPayload = false;


class GBDialogElement extends HTMLElement {
  static get observedAttributes() {
    return ['open', 'dialog-title'];
  }

  constructor() {
    super();

    this.dialogElement = null;
    this.titleElement = null;

    this.onClickDialog = (event) => {
      if (event.target === event.currentTarget) {
        this.open = false;
      }
    };

    this.onDialogTransitionFinished = () => {
      if (!this.open) {
        this.dialogElement.close();
        this.dispatchEvent(
          new CustomEvent(EventModalClosed, {
            detail: undefined,
            bubbles: true,
            composed: true,
          })
        );
      }
    };

    this.closingDialogTriggered = () => {
      this.open = false;
    };

    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = template.cloneNode(true).innerHTML;
  }

  connectedCallback() {
    this.dialogElement = this.shadowRoot.querySelector('dialog');
    this.titleElement = this.shadowRoot.querySelector('.title');
    this.internalCloseButton = this.shadowRoot.querySelector('[data-close-button]');
    this.closeButtons = document.querySelectorAll('[data-close-button]');

    if (this.open) {
      this.dialogElement?.showModal();
    }

    if (this.titleElement) {
      this.dialogTitleChanged();
    }

    this._onClickCloseButton = this.onClickCloseButton.bind(this);
    if (this.internalCloseButton) {
        this.internalCloseButton.addEventListener('click', this._onClickCloseButton);
    }

    var self = this;

    if (this.closeButtons && this.closeButtons.length) {
        this.closeButtons.forEach(function(button) {
            button.addEventListener('click', self._onClickCloseButton);
        });
    }
 
    this.dialogElement.addEventListener('click', this.onClickDialog);
    this.dialogElement.addEventListener('transitionend', this.onDialogTransitionFinished);
    this.dialogElement.addEventListener('close', this.closingDialogTriggered);
  }

  resetScrollTop() {
    this.dialogElement.scrollTop = 0;
  }

  attributeChangedCallback(name) {
    switch (name) {
      case 'open':
        this.openChanged();
        break;
      case 'dialog-title':
        this.dialogTitleChanged();
        break;
    }
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(open) {
    if (open) {
      this.setAttribute('open', '');
    } else {
      this.removeAttribute('open');
    }

    this.dispatchEvent(
      new CustomEvent(EventToggleOpen, {
        detail: open,
        bubbles: true,
        composed: true,
      })
    );
  }

  openChanged() {
    if (!this.dialogElement) return;

    this.dispatchEvent(new CustomEvent("dialog-closing", {
      detail: this.open,
      bubbles: true,
      composed: true
    }));

    if (this.open) {
      this.dialogElement.showModal();
      this.dialogElement.classList.add('fade-in');
    } else {
      this.dialogElement.classList.remove('fade-in');
    }
  }

  get dialogTitle() {
    return this.getAttribute('dialog-title') || '...';
  }

  set dialogTitle(title) {
    this.setAttribute('dialog-title', title);
  }

  dialogTitleChanged() {
    if (!this.titleElement) return;
    this.titleElement.textContent = this.dialogTitle;
  }

  onClickCloseButton() {
    this.open = false;
  }

  disconnectedCallback() {
    if (!this.dialogElement) return;
    this.dialogElement.removeEventListener('click', this.onClickDialog);
    this.dialogElement.removeEventListener('transitionend', this.onDialogTransitionFinished);
    this.dialogElement.removeEventListener('close', this.closingDialogTriggered);

    if (this.internalCloseButton) {
        this.internalCloseButton.removeEventListener('click', this._onClickCloseButton);
    }

    var self = this;

    if (this.closeButtons && this.closeButtons.length) {
        this.closeButtons.forEach(function(button) {
            button.removeEventListener('click', self._onClickCloseButton);
        });
        this._onClickCloseButton = null;
    }
 
  }
}

customElements.define('gb-dialog', GBDialogElement);
